import { getApiBaseUrl } from '../config/apiConfig';
import { HTTP_STATUS } from '../constants/httpStatus';
import type {
  ApiConfig,
  DeleteScheduleResponse,
  GetScheduleListQuery,
  GetScheduleResponse,
  OwnershipApiClient,
  PatchScheduleRequest,
  PatchScheduleResponse,
  PostScheduleRequest,
  PostScheduleResponse,
  ScheduleAvailResponse,
  ScheduleInfoResponse,
  ScheduleLimitsResponse,
} from '../types/api';
import { isDevelopment } from '../utils/environment';
import { ApiError, BaseApiClient } from './apiClient';

function toQueryParams(query: GetScheduleListQuery): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value != null) {
      result[key] = value as string | number | boolean;
    }
  }
  return result;
}

/**
 * Service for managing room ownership and scheduling
 */
export class OwnershipService extends BaseApiClient implements OwnershipApiClient {
  /** Allow more time for slow / unstable connections before aborting (SM-932). */
  private static readonly REQUEST_TIMEOUT_MS = 20000;

  constructor(config?: Partial<ApiConfig>) {
    const isDevMode = isDevelopment();

    super({
      baseUrl: getApiBaseUrl(isDevMode),
      timeout: OwnershipService.REQUEST_TIMEOUT_MS,
      ...config,
    });
  }

  /**
   * Get room ownership information
   */
  async getSchedule(room: string, detailed?: boolean): Promise<GetScheduleResponse> {
    const params: Record<string, string | number | boolean> = { room };
    if (detailed !== undefined) {
      params.detailed = detailed;
    }
    const response = await this.get<GetScheduleResponse>('/ownership/app/schedule', params);
    return response.data;
  }

  /**
   * Schedule a new room
   */
  async createSchedule(request: PostScheduleRequest): Promise<PostScheduleResponse> {
    const response = await this.post<PostScheduleResponse>('/ownership/app/schedule', request);
    return response.data;
  }

  /**
   * Delete a scheduled room
   */
  async deleteSchedule(room: string): Promise<DeleteScheduleResponse> {
    const response = await this.delete<DeleteScheduleResponse>('/ownership/app/schedule', { room });
    return response.data;
  }

  /**
   * Retrieve schedule list for the authenticated user
   */
  async getScheduleList(query?: GetScheduleListQuery): Promise<GetScheduleResponse[]> {
    const params = query ? toQueryParams(query) : undefined;
    const response = await this.get<GetScheduleResponse[]>('/ownership/app/schedule-list', params);
    return response.data;
  }

  /**
   * Retrieve only room names for the authenticated user
   */
  async getScheduleNames(query?: GetScheduleListQuery): Promise<string[]> {
    const params = query ? toQueryParams(query) : undefined;
    const response = await this.get<string[]>('/ownership/app/schedule-names', params);
    return response.data;
  }

  /**
   * Get schedule information for a room
   */
  async getScheduleInfo(room: string): Promise<ScheduleInfoResponse> {
    const response = await this.get<ScheduleInfoResponse>('/ownership/app/schedule-info', { room });
    return response.data;
  }

  /**
   * Check whether a room name is free, invalid, or already in use.
   * The endpoint returns an empty body, so the result is read from the status code.
   */
  async getScheduleAvailability(room: string): Promise<ScheduleAvailResponse> {
    try {
      await this.get('/ownership/app/schedule-avail', { room });
      return 'RoomIsFree';
    } catch (error) {
      if (!(error instanceof ApiError)) {
        throw error;
      }
      switch (error.status) {
        case HTTP_STATUS.badRequest:
          return 'RoomIsInvalid';
        case HTTP_STATUS.conflict:
          return 'RoomIsInUse';
        default:
          throw error;
      }
    }
  }

  /**
   * Update scheduled meeting
   */
  async updateSchedule(request: PatchScheduleRequest): Promise<PatchScheduleResponse> {
    const response = await this.patch<PatchScheduleResponse>('/ownership/app/schedule', request);
    return response.data;
  }

  /**
   * Check if user owns a specific room
   */
  async isRoomOwner(room: string): Promise<boolean> {
    try {
      const scheduleInfo = await this.getScheduleInfo(room);
      return scheduleInfo.role === 'owner';
    } catch (_error) {
      return false;
    }
  }

  /**
   * Check if user is a moderator for a specific room
   */
  async isRoomModerator(room: string): Promise<boolean> {
    try {
      const scheduleInfo = await this.getScheduleInfo(room);
      return scheduleInfo.role === 'owner' || scheduleInfo.role === 'moderator';
    } catch (_error) {
      return false;
    }
  }

  /**
   * Get all rooms owned by the current user
   */
  async getOwnedRooms(): Promise<GetScheduleResponse[]> {
    return this.getScheduleList();
  }

  /**
   * Create a personal meeting room
   */
  async createPersonalRoom(
    roomName: string,
    moderatorEmail: string,
    options?: {
      description?: string;
      password?: string;
      restricted?: boolean;
      options?: Record<string, unknown>;
      features?: Record<string, unknown>;
    },
  ): Promise<PostScheduleResponse> {
    const request: PostScheduleRequest = {
      room: roomName,
      moderators: [moderatorEmail],
      description: options?.description || `Personal meeting room for ${moderatorEmail}`,
      password: options?.password ?? '',
      restricted: options?.restricted,
      options: options?.options || {},
      features: options?.features || {},
    };
    return this.createSchedule(request);
  }

  /**
   * Get schedule limits (e.g., max personal meetings)
   */
  async getScheduleLimits(): Promise<ScheduleLimitsResponse> {
    const response = await this.get<ScheduleLimitsResponse>('/ownership/app/schedule-limits');
    return response.data;
  }
}

// Create singleton instance
export const ownershipService = new OwnershipService();
